/**
 * SiteDashboardPage (CLOUD) — the selected site at a glance: the same six summary cards its row
 * shows on the all-sites dashboard, and the same page the local product opens on.
 *
 * It exists so "Dashboard" means something in both scopes. All Sites answers "which site needs
 * me"; this answers "how is THIS site" — and an operator who has switched scope to one site
 * shouldn't have to go back to the estate view and hunt for its row to see its numbers.
 *
 * The reads are the same four the all-sites dashboard fans out per node, run under `allSettled`
 * so one denied or timed-out tunnel call leaves its own card empty rather than blanking the page.
 * Host metrics come from the node registry (which NodeContext already polls) rather than a fifth
 * tunnel call — the cloud knows a node's load without asking it.
 *
 * They sit under two titled sheets: AI ANALYTICS — what that site's AI has seen today, the same
 * dynamic card row the local dashboard draws, from the same shared component and the same single
 * /analytics/summary call, proxied through this site's tunnel — and SYSTEM PERFORMANCE, the
 * site's own load and inventory. The analytics window is the SITE's midnight, decided on the
 * node, so a site in another timezone reports its own day rather than the operator's.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import {
  NodeSummaryCards,
  formatUptime,
  type NodeSummaryData,
  AiAnalyticsCards,
  type AiAnalyticsSummary,
  LatestAlertsStrip,
  type AlertPlace,
  Modal,
  RecordPager,
  useActiveModelSurfaces,
  useApi,
  useLatestAlerts,
  describeApiError,
} from '@trinetra/shared';
import { AlertDetailView } from './AlertDetailPage';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getNodeCameras,
  getNodeGroups,
  getNodeModelAssignments,
  getNodeAlerts,
  getNodeAiSummary,
} from '../services/api.service';

/** Value of a fulfilled settled result, or undefined if it rejected. */
const settled = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' ? r.value : undefined);

/** The node proxy answers with a bare array, `{data}` or `{data, pagination}`. */
function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

/**
 * A sheet's heading: what this group of tiles is, and the one qualification the tiles
 * themselves can't carry — the window they cover, or whose machine they describe.
 *
 * On the divider rather than floating above the sheet, so the title is visibly attached to the
 * cards it names; two sheets stacked with free-floating headings read as four things.
 */
function SectionHead({ title, caption }: { title: string; caption?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
      <h2 className="text-[13px] font-semibold text-foreground">{title}</h2>
      {caption && <p className="text-[11px] text-muted-foreground">{caption}</p>}
    </div>
  );
}

export default function SiteDashboardPage() {
  const { nodes, selectedNodeId } = useNode();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  // Read the node out of `nodes` rather than `selectedNode`: that object is memoised on
  // id/status/name for reference stability, so its `metrics` would be whatever the poll happened
  // to carry when the name last changed — stale by minutes on a page whose job is load.
  const node = useMemo(
    () => nodes.find((n) => n.nodeId === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );
  const isOnline = node?.status === 'online';

  const [data, setData] = useState<NodeSummaryData>({
    cameras: [], groups: [], assignments: {}, alerts: [], loading: true,
  });
  const [failed, setFailed] = useState<string[]>([]);

  const canCameras = hasPermission('cameras.view');
  const canGroups = hasPermission('groups.view');
  const canModels = hasPermission('ai-models.view');
  const canAlerts = hasPermission('alerts.view');
  // One key for the whole analytics row: the node's command-center has no per-user camera scope,
  // so these are site-wide figures for anyone entitled to see AI events at all.
  const canAiEvents = hasPermission('ai_events.view');
  const canAttendance = hasPermission('attendance.view');

  // Per-node registry, so the hook is keyed on the selected site — switching sites refetches.
  const { surfaces } = useActiveModelSurfaces(selectedNodeId);
  // The analytics payload in its two halves — see loadAiCore / loadAiSurfaces below.
  const [aiCore, setAiCore] = useState<AiAnalyticsSummary | null>(null);
  const [aiSurfaces, setAiSurfaces] = useState<AiAnalyticsSummary | null>(null);

  /**
   * The strip at the top of the page: this site's still-open alerts, LIVE.
   *
   * Deliberately not fed from `load()`'s alert fetch below — that is a 30-second poll behind a
   * tunnel, and a strip showing the picture of what is happening right now can't be half a
   * minute behind. The same adapter the alert board uses gives it the node's event socket
   * instead, so an alert lands here when it lands there and acknowledging it in either place
   * clears it in both.
   *
   * `nodeKey` is what re-subscribes on a site switch; `blocked` holds every request while no
   * site is selected or the one selected is offline — there is nothing on the far end of the
   * tunnel to ask.
   */
  const { hexAlerts } = useApi();
  const latest = useLatestAlerts({
    api: hexAlerts,
    blocked: !canAlerts || !selectedNodeId || !isOnline,
    nodeKey: selectedNodeId || undefined,
  });

  /**
   * Which alert's record is open, as a CURSOR into the strip's list rather than one alert.
   *
   * The record opens in a modal, the same way it does from the alert board: routing to
   * /alerts/:id would put the operator's Back button on the alert LIST rather than back on the
   * page they were reading. Holding the index also gives the modal its pager.
   */
  const [viewingIndex, setViewingIndex] = useState<number | null>(null);
  // Resolved against the CURRENT feed, so an alert cleared under the modal closes it instead of
  // showing a record that is no longer open.
  const viewable = useMemo(() => latest.alerts.filter((a) => !!a.id), [latest.alerts]);
  const viewing =
    viewingIndex == null ? null : viewable[Math.min(viewingIndex, viewable.length - 1)] || null;

  const load = useCallback(async () => {
    if (!selectedNodeId || !isOnline) {
      setData({ cameras: [], groups: [], assignments: {}, alerts: [], loading: false });
      return;
    }
    const [cams, groups, assignments, alerts] = await Promise.allSettled([
      canCameras ? getNodeCameras(selectedNodeId) : Promise.resolve([]),
      canGroups ? getNodeGroups(selectedNodeId) : Promise.resolve([]),
      canModels ? getNodeModelAssignments(selectedNodeId) : Promise.resolve({}),
      canAlerts ? getNodeAlerts(selectedNodeId, { limit: 5, sort: 'desc' }) : Promise.resolve({ data: [] }),
    ]);

    setData({
      cameras: toArray(settled(cams)),
      groups: toArray(settled(groups)),
      assignments: settled(assignments) || {},
      alerts: toArray(settled(alerts)),
      loading: false,
    });

    // Named, because "no alerts" and "the tunnel didn't answer" look identical on a card.
    setFailed(
      [
        cams.status === 'rejected' && 'cameras',
        groups.status === 'rejected' && 'groups',
        assignments.status === 'rejected' && 'AI models',
        alerts.status === 'rejected' && 'alerts',
      ].filter(Boolean) as string[],
    );
  }, [selectedNodeId, isOnline, canCameras, canGroups, canModels, canAlerts]);

  useEffect(() => { load(); }, [load]);

  /**
   * The analytics row on its own requests, for the same reasons the local dashboard splits it —
   * and here every one of them is a TUNNEL round trip, so the split matters more.
   *
   * The per-model figures must name the surfaces this site runs, so they wait on the model
   * registry: a second hop, through the same tunnel, before the request can even be issued.
   * The alert and presence figures need no registry at all. Asked for together they inherited
   * that wait and the whole row sat in skeletons; asked separately, the half that can answer
   * immediately does. An offline site skips both — the summary lives on the node.
   */
  const noteAiFailure = useCallback((label: string, err: unknown) => {
    // Keep the last good figures rather than blanking the row on one flapped tunnel call — but
    // say WHY. "Couldn't read AI summary" is the same sentence whether the site is offline, the
    // tunnel timed out, the role lacks ai_events.view, or the command-center rejected the
    // service token, and those have four different fixes.
    const reason = `${label} (${describeApiError(err)})`;
    setFailed((prev) => (prev.some((f) => f.startsWith(label)) ? prev : [...prev, reason]));
  }, []);

  const loadAiCore = useCallback(async (fresh = false) => {
    if (!selectedNodeId || !isOnline || !canAiEvents) {
      setAiCore(null);
      return;
    }
    try {
      setAiCore(
        await getNodeAiSummary(selectedNodeId, [], { alerts: canAlerts, attendance: canAttendance, fresh }),
      );
    } catch (err) {
      noteAiFailure('AI summary', err);
    }
  }, [selectedNodeId, isOnline, canAiEvents, canAlerts, canAttendance, noteAiFailure]);

  const loadAiSurfaces = useCallback(async (fresh = false) => {
    if (!selectedNodeId || !isOnline || !canAiEvents || surfaces === null) {
      setAiSurfaces(null);
      return;
    }
    if (surfaces.length === 0) return;
    try {
      setAiSurfaces(
        await getNodeAiSummary(selectedNodeId, surfaces, { alerts: false, attendance: false, fresh }),
      );
    } catch (err) {
      noteAiFailure('AI model figures', err);
    }
  }, [selectedNodeId, isOnline, canAiEvents, surfaces, noteAiFailure]);

  useEffect(() => { loadAiCore(); }, [loadAiCore]);
  useEffect(() => { loadAiSurfaces(); }, [loadAiSurfaces]);

  /**
   * The counts follow the FEED, not the clock.
   *
   * The strip is a socket and the analytics figures are a 30-second poll of an aggregate, so an
   * alert landed in the strip instantly and the "needs attention" count beside it stayed a
   * request behind for most of a minute — which reads as one of the two being broken rather
   * than as two refresh strategies. `revision` bumps whenever the live feed actually moves (an
   * arrival, an ack, a resolve, a node-side wipe), so the aggregates are re-read then instead
   * of at the next tick.
   *
   * Debounced, because alerts arrive in bursts and a re-read per alert would be a request storm
   * for a number that only has to be right once the burst settles. `fresh` because the point is
   * a figure that includes what just happened, and the node's cache is up to ten seconds older
   * than that.
   */
  const alertRevision = latest.revision;
  useEffect(() => {
    if (!alertRevision) return; // nothing has happened yet — the mount fetch covers the rest
    const id = setTimeout(() => {
      loadAiCore(true);
      loadAiSurfaces(true);
    }, 1500);
    return () => clearTimeout(id);
  }, [alertRevision, loadAiCore, loadAiSurfaces]);

  /** The two halves as the one payload the card row takes. */
  const aiSummary = useMemo<AiAnalyticsSummary | null>(() => {
    if (!aiCore && !aiSurfaces) return null;
    const base = (aiCore || aiSurfaces) as AiAnalyticsSummary;
    return {
      ...base,
      surfaces: aiSurfaces?.surfaces || {},
      alerts: aiCore?.alerts ?? null,
      attendance: aiCore?.attendance ?? null,
    };
  }, [aiCore, aiSurfaces]);

  /**
   * camera id → name, for the alert lines in the analytics section. The node's alerts carry a
   * camera id and no name (they are denormalised for query speed), and this page already holds
   * the site's camera list for the cards below.
   */
  const cameraNames = useMemo(
    () => Object.fromEntries((data.cameras || []).map((c: any) => [c.id, c.name]).filter(([, nm]) => nm)),
    [data.cameras],
  );

  /**
   * camera id → where it is, for the alert strip's "Main Gate • Pune Plant" line.
   *
   * The area is the ROOT of the camera's primary group tree, resolved from the flat group list
   * this page already holds — no second tunnel call for a caption. A camera in no group simply
   * shows its own name.
   */
  const alertPlaces = useMemo(() => {
    const byId: Record<string, any> = {};
    (data.groups || []).forEach((g: any) => g?.id && (byId[g.id] = g));
    const rootOf = (groupId: string): string => {
      let cur = groupId;
      const seen = new Set<string>();
      while (byId[cur]?.parent_id && !seen.has(cur)) {
        seen.add(cur);
        cur = byId[cur].parent_id;
      }
      return cur;
    };
    const places: Record<string, AlertPlace> = {};
    (data.cameras || []).forEach((c: any) => {
      if (!c?.id) return;
      const groupId = c.groups?.[0]?.id ?? null;
      const area = groupId ? byId[rootOf(groupId)]?.name : undefined;
      // A camera whose only group IS the area would otherwise read "Main Gate • Main Gate".
      places[c.id] = { name: c.name, site: area && area !== c.name ? area : undefined };
    });
    return places;
  }, [data.cameras, data.groups]);

  // Same cadence as the all-sites dashboard, paused while hidden — each refresh is four tunnel
  // round-trips to a machine that has other work to do.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      load();
      loadAiCore();
      loadAiSurfaces();
      // The strip's list is live; only the open COUNT can drift (alerts cleared by another
      // operator while this tab never heard the lifecycle event).
      latest.refresh();
    }, 30_000);
    return () => clearInterval(id);
  }, [load, loadAiCore, loadAiSurfaces, latest.refresh]);

  /** The site's own section: drawn for anyone who may see any part of what's in it. */
  const showMachine =
    hasPermission('monitoring.view') || canCameras || canModels || canGroups;

  /** Shared by both branches below, so the tiles are identical whether or not AI is permitted. */
  const nodeCardProps = {
    data: { ...data, metrics: node?.metrics || {} },
    links: {
      cameras: canCameras ? '/camera-management' : undefined,
      recording: hasPermission('recordings.view') ? '/recordings' : undefined,
      models: hasPermission('ai-models.assign') ? '/model-assignments' : undefined,
      groups: canGroups ? '/camera-groups' : undefined,
      status: hasPermission('monitoring.view') ? `/node-monitor/${selectedNodeId}` : undefined,
    },
    // Already scoped to this site, so no selectNode dance — unlike the all-sites row.
    onNavigate: navigate,
  };

  return (
    <>
    <div className="space-y-4 p-3 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-foreground">
            {node?.name || 'Dashboard'}
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {!selectedNodeId
              ? 'Select a site to see its summary.'
              : isOnline
                ? `This site at a glance${node?.metrics?.uptimeSeconds ? ` · up ${formatUptime(node.metrics.uptimeSeconds)}` : ''}`
                : 'This site is offline.'}
          </p>
        </div>
        <button
          /* `fresh` on the button and nowhere else: Refresh must recompute, or it hands back
             the same cached figures the operator just decided were not good enough. */
          onClick={() => { load(); loadAiCore(true); loadAiSurfaces(true); }}
          title="Refresh"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </div>

      {failed.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
          Couldn't read {failed.join(', ')} from this site — those cards may be empty or out of date.
        </div>
      )}

      {!selectedNodeId ? (
        <div className="rounded-xl border border-border bg-card p-5 text-[13px] text-muted-foreground shadow-card">
          No site selected. Pick one from the site switcher in the title bar.
        </div>
      ) : !isOnline ? (
        <div className="rounded-xl border border-border bg-card p-3 shadow-card">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-3 text-[12px] text-muted-foreground">
            <NoSymbolIcon className="h-4 w-4 flex-none" />
            <span>Offline — cameras, alerts and metrics are unavailable until this site reconnects.</span>
          </div>
        </div>
      ) : (
        /* TWO SHEETS, EACH WITH ITS NAME ON IT — the same structure the local dashboard uses,
           so an operator moving between the products reads the same page.
           These tiles spent a while sharing one unlabelled grid, on the reasoning that every
           card is about this site anyway. What that missed is that they answer two different
           KINDS of question — what the cameras saw (a count of events, over a window, that
           changes by the minute) and how the machine is bearing it (meters and inventory, true
           right now and with no window at all) — and a reader who can't see the seam ends up
           reading a CPU meter as part of today's tally. A heading each says which is which,
           and the split lets the analytics row keep its own "since midnight at this site"
           caption without it appearing to govern the meters. */
        <>
          {/* THE ALERTS THEMSELVES, FIRST. Everything below is a count of what has happened;
              this is what is happening. Above the sheets rather than inside one, because it is
              not a summary of the site — it is the site interrupting. */}
          {canAlerts && (
            <LatestAlertsStrip
              alerts={latest.alerts}
              open={latest.open}
              loading={latest.loading}
              places={alertPlaces}
              /* A node's snapshot URLs point inside that node, so the cloud can't load them
                 directly — the adapter streams each frame through the tunnel as a blob, and
                 the card resolves it lazily once it is on screen. */
              loadPreview={hexAlerts.loadPreview}
              onOpenAlert={(a) => {
                if (!a.id) { navigate(`/alerts?camera_id=${a.cameraId}`); return; }
                const at = viewable.findIndex((v) => v.id === a.id);
                setViewingIndex(at >= 0 ? at : 0);
              }}
              onAcknowledge={latest.canAcknowledge ? latest.acknowledge : undefined}
              onResolve={latest.canResolve ? latest.resolve : undefined}
              onOpenBoard={() => navigate('/alert-board')}
              /* The count is the whole queue: the pill opens the list, filtered to it. */
              onViewAll={() => navigate('/alerts?status=new')}
              /* The strip scrolls sideways: reaching the end of the live feed pulls the
                 next page of older alerts, and nothing is fetched before that. */
              onLoadMore={latest.hasMore ? latest.loadMore : undefined}
              loadingMore={latest.loadingMore}
            />
          )}

          {canAiEvents && (
            <section className="rounded-xl border border-border bg-card shadow-card">
              {/* The window is not decoration: the model cards run on the SITE's calendar day,
                  presence on the attendance work date with its own cutoff hour, and both are
                  the site's clock rather than this browser's. An operator in another timezone
                  reading unlabelled figures would take them for their own day. */}
              <SectionHead
                title="AI Analytics"
                caption={`Today, since midnight at this site${
                  aiSummary?.attendance?.work_date
                    ? ` · presence for work day ${aiSummary.attendance.work_date}`
                    : ''
                }`}
              />
              <AiAnalyticsCards
                className="p-3"
                surfaces={surfaces}
                summary={aiSummary}
                latestAlerts={canAlerts ? data.alerts : undefined}
                cameraNames={cameraNames}
                /* Two flags, because the two halves land at different times: the alert and
                   presence cards paint as soon as the core call returns, and only the model
                   cards wait on the registry + their own request. */
                loading={!aiCore}
                surfacesLoading={surfaces === null || (surfaces.length > 0 && !aiSurfaces)}
                links={{
                  reports: {
                    face: '/reports/face',
                    person: '/reports/person',
                    'person-count': '/reports/person-count',
                    intrusion: '/reports/intrusion',
                    'vehicle-crossing': '/reports/vehicle-crossing',
                    'ppe-violation': '/reports/ppe-violation',
                    theft: '/reports/theft',
                    fall: '/reports/fall',
                    'fire-smoke': '/reports/fire-smoke',
                  },
                  alerts: canAlerts ? '/alerts' : undefined,
                  attendance: canAttendance ? '/attendance/presence' : undefined,
                }}
                onNavigate={navigate}
              />
            </section>
          )}

          {showMachine && (
            <section className="rounded-xl border border-border bg-card shadow-card">
              {/* The metrics come from the node registry the cloud already polls, not from the
                  site itself — which is why they survive a slow tunnel. */}
              <SectionHead
                title="System Performance"
                caption="This site — live load, cameras and AI models"
              />
              {/* ONE panel, three columns: what this site has, what the AI is pointed at, and how
                  it is bearing it. They were two tiles that each merged several answers — the
                  ring, the streams, the model counts and the meters all crushed into "Cameras &
                  AI Models" beside "System Health" — which put unrelated figures under one
                  heading and split related ones across two. Columns of a single sheet let each
                  keep its own heading and its own icon while still reading as one block. */}
              <NodeSummaryCards panel cards={['cameras', 'models-groups', 'status']} {...nodeCardProps} />
            </section>
          )}
        </>
      )}
    </div>

    {/* The alert's record, over the dashboard. Same modal the alert board opens, for the same
        reason: this page is a vantage point, and routing away from it to /alerts/:id puts the
        operator's Back button on the alert list instead of back where they were. */}
    <Modal
      open={!!viewing}
      onClose={() => setViewingIndex(null)}
      title={viewing && (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-brand">{viewing.alertName || 'Alert'}</span>
          <span className="text-[13px] font-normal text-muted-foreground">· Alert Details</span>
        </span>
      )}
      ariaLabel="Alert details"
      subtitle={viewing ? alertPlaces[viewing.cameraId]?.name : undefined}
      size="full"
      headerActions={viewing && viewingIndex != null && (
        <>
          {/* Walks the alerts the strip is showing — buttons and the arrow keys both. */}
          <RecordPager
            index={Math.min(viewingIndex, viewable.length - 1)}
            count={viewable.length}
            onIndexChange={setViewingIndex}
            noun="alerts"
          />
          <button
            type="button"
            onClick={() => navigate(`/alerts/${viewing.id}`)}
            title="Open the full alert page"
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
          </button>
        </>
      )}
    >
      {/* Keyed on the alert id so paging to another one refetches instead of showing the
          previous alert's data while the new one loads. */}
      {viewing?.id && <AlertDetailView key={viewing.id} alertId={viewing.id} showTitle={false} />}
    </Modal>
    </>
  );
}
